import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BankLayout from './BankLayout';
import { signupUser } from './apiClient';

const initialForm = {
  fullName: '',
  username: '',
  email: '',
  mobile: '',
  customerId: '',
  branch: '',
  accountType: 'Savings Account',
  password: '',
  confirmPassword: '',
};

function SignupPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setForm(current => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setError('');

    if (Object.values(form).some(value => !String(value).trim())) {
      setError('Complete all registration details before continuing.');
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError('Password and confirmation must match.');
      return;
    }

    setIsSubmitting(true);

    try {
      await signupUser({
        fullName: form.fullName,
        username: form.username,
        email: form.email,
        mobile: form.mobile,
        customerId: form.customerId,
        branch: form.branch,
        accountType: form.accountType,
        password: form.password,
      });
      navigate('/account');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Registration failed. Try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BankLayout
      pageTitle="New User Registration"
      strapline="Create a demo retail banking profile and land directly in the account dashboard."
    >
      <section style={styles.card}>
        <div style={styles.cardHeader}>Register For Digital Banking</div>
        <form style={styles.form} onSubmit={handleSubmit}>
          <label style={styles.label}>
            Full Name
            <input
              style={styles.input}
              value={form.fullName}
              onChange={event => handleChange('fullName', event.target.value)}
              placeholder="Enter full name"
            />
          </label>

          <label style={styles.label}>
            Preferred Username
            <input
              style={styles.input}
              value={form.username}
              onChange={event => handleChange('username', event.target.value)}
              placeholder="Create username"
            />
          </label>

          <label style={styles.label}>
            Email Address
            <input
              style={styles.input}
              type="email"
              value={form.email}
              onChange={event => handleChange('email', event.target.value)}
              placeholder="name@example.com"
            />
          </label>

          <label style={styles.label}>
            Mobile Number
            <input
              style={styles.input}
              value={form.mobile}
              onChange={event => handleChange('mobile', event.target.value)}
              placeholder="+91 98XXXXXXXX"
            />
          </label>

          <label style={styles.label}>
            CIF / Customer ID
            <input
              style={styles.input}
              value={form.customerId}
              onChange={event => handleChange('customerId', event.target.value)}
              placeholder="Enter customer identifier"
            />
          </label>

          <label style={styles.label}>
            Home Branch
            <input
              style={styles.input}
              value={form.branch}
              onChange={event => handleChange('branch', event.target.value)}
              placeholder="Branch name"
            />
          </label>

          <label style={styles.label}>
            Account Type
            <select
              style={styles.input}
              value={form.accountType}
              onChange={event => handleChange('accountType', event.target.value)}
            >
              <option>Savings Account</option>
              <option>Salary Account</option>
              <option>Current Account</option>
            </select>
          </label>

          <label style={styles.label}>
            Login Password
            <input
              style={styles.input}
              type="password"
              value={form.password}
              onChange={event => handleChange('password', event.target.value)}
              placeholder="Create password"
            />
          </label>

          <label style={styles.label}>
            Confirm Password
            <input
              style={styles.input}
              type="password"
              value={form.confirmPassword}
              onChange={event => handleChange('confirmPassword', event.target.value)}
              placeholder="Confirm password"
            />
          </label>

          {error ? <div style={styles.error}>{error}</div> : null}

          <div style={styles.actions}>
            <button type="submit" style={styles.primaryButton}>
              {isSubmitting ? 'Creating Profile...' : 'Register And Continue'}
            </button>
          </div>
        </form>
      </section>
    </BankLayout>
  );
}

const styles = {
  card: {
    background: '#fff',
    border: '1px solid var(--sbi-border)',
    borderRadius: 14,
    overflow: 'hidden',
  },
  cardHeader: {
    background: 'linear-gradient(180deg, #2a63b0 0%, var(--sbi-blue) 100%)',
    color: '#fff',
    fontSize: 18,
    fontWeight: 700,
    padding: '14px 18px',
  },
  form: {
    display: 'grid',
    gap: 16,
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    padding: 22,
  },
  label: {
    color: 'var(--sbi-deep-blue)',
    display: 'block',
    fontSize: 14,
    fontWeight: 700,
  },
  input: {
    border: '1px solid #b7c7e3',
    borderRadius: 8,
    display: 'block',
    marginTop: 8,
    padding: '12px 13px',
    width: '100%',
  },
  error: {
    color: '#c43b23',
    fontSize: 13,
    fontWeight: 700,
    gridColumn: '1 / -1',
  },
  actions: {
    gridColumn: '1 / -1',
  },
  primaryButton: {
    background: 'linear-gradient(180deg, #f2ba52 0%, var(--sbi-gold) 100%)',
    border: '1px solid #cb8f1b',
    borderRadius: 8,
    color: '#21314d',
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 700,
    padding: '12px 22px',
  },
};

export default SignupPage;
